import { Button, Container } from '@playcanvas/pcui';

import { Events } from '../events';

type Picture = {
    id: string,
    image?: {
        url?: string
    },
    position?: {
        x: number,
        y: number,
        z: number
    },
    target?: {
        x: number,
        y: number,
        z: number
    },
    fov?: number
};

class ComparisonSlider extends Container {
    private readonly events: Events;
    private readonly image: HTMLImageElement;
    private dragging = false;

    private readonly onOpenBound: () => void;
    private readonly onCloseBound: () => void;
    private readonly onToggleBound: () => void;
    private readonly onMouseMoveBound: (event: MouseEvent) => void;
    private readonly onMouseUpBound: () => void;
    private readonly onPointerMoveBound: (event: PointerEvent) => void;
    private readonly onPointerUpBound: () => void;
    private readonly onKeyDownBound: (event: KeyboardEvent) => void;
    private readonly onKeyUpBound: (event: KeyboardEvent) => void;

    constructor(events: Events) {
        super({
            id: 'picture-compare-overlay',
            hidden: true
        });

        this.events = events;

        const viewport = document.createElement('div');
        viewport.id = 'picture-compare-viewport';

        const image = document.createElement('img');
        image.id = 'picture-compare-image';
        image.alt = 'Evidence comparison image';
        image.draggable = false;
        viewport.appendChild(image);
        this.image = image;

        const divider = document.createElement('div');
        divider.id = 'picture-compare-divider';
        const grip = document.createElement('div');
        grip.id = 'picture-compare-divider-grip';
        divider.appendChild(grip);
        viewport.appendChild(divider);

        const closeButton = new Button({
            id: 'picture-compare-close',
            text: 'Close'
        });

        this.dom.appendChild(viewport);
        this.append(closeButton);

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        divider.addEventListener('pointerdown', (event) => {
            event.preventDefault();
            this.dragging = true;
            this.setSplitFromClientX(event.clientX);
        });

        divider.addEventListener('mousedown', (event) => {
            event.preventDefault();
            this.dragging = true;
            this.setSplitFromClientX(event.clientX);
        });

        closeButton.on('click', () => this.close());

        this.onPointerMoveBound = (event: PointerEvent) => {
            if (!this.dragging) {
                return;
            }
            this.setSplitFromClientX(event.clientX);
        };

        this.onPointerUpBound = () => {
            this.dragging = false;
        };

        this.onMouseMoveBound = (event: MouseEvent) => {
            if (!this.dragging) {
                return;
            }
            this.setSplitFromClientX(event.clientX);
        };

        this.onMouseUpBound = () => {
            this.dragging = false;
        };

        this.onKeyDownBound = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !this.hidden) {
                this.close();
            }
        };

        this.onKeyUpBound = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !this.hidden) {
                this.close();
            }
        };

        window.addEventListener('pointermove', this.onPointerMoveBound);
        window.addEventListener('pointerup', this.onPointerUpBound);
        window.addEventListener('mousemove', this.onMouseMoveBound);
        window.addEventListener('mouseup', this.onMouseUpBound);
        window.addEventListener('keydown', this.onKeyDownBound, true);
        document.addEventListener('keydown', this.onKeyDownBound, true);
        window.addEventListener('keyup', this.onKeyUpBound, true);
        document.addEventListener('keyup', this.onKeyUpBound, true);

        this.onOpenBound = () => this.openFromSelection();
        this.onCloseBound = () => this.close();
        this.onToggleBound = () => {
            if (this.hidden) {
                this.openFromSelection();
            } else {
                this.close();
            }
        };

        events.on('evidenceComparison.open', this.onOpenBound);
        events.on('evidenceComparison.close', this.onCloseBound);
        events.on('evidenceComparison.toggle', this.onToggleBound);
        events.function('evidenceComparison.visible', () => !this.hidden);

        this.setSplit(50);
    }

    private setSplit(value: number) {
        const clamped = Math.max(0, Math.min(100, value));
        this.dom.style.setProperty('--compare-split', `${clamped}%`);
        this.dom.dataset.split = clamped.toFixed(2);
    }

    private setSplitFromClientX(clientX: number) {
        const rect = this.dom.getBoundingClientRect();
        if (rect.width <= 0) {
            return;
        }
        const ratio = (clientX - rect.left) / rect.width;
        this.setSplit(ratio * 100);
    }

    private openFromSelection() {
        const selected = this.events.invoke('pictures.selected') as Picture | null;
        if (!selected?.image?.url) {
            return;
        }

        if (selected.position && selected.target) {
            this.events.fire('camera.setPose', {
                position: selected.position,
                target: selected.target
            }, 0);
        }

        if (typeof selected.fov === 'number' && Number.isFinite(selected.fov)) {
            this.events.fire('camera.setFov', selected.fov);
        }

        this.image.src = selected.image.url;
        this.setSplit(50);
        this.hidden = false;
        this.events.fire('evidenceComparison.visibleChanged', true);
    }

    private close() {
        if (this.hidden) {
            return;
        }
        this.dragging = false;
        this.hidden = true;
        this.events.fire('evidenceComparison.visibleChanged', false);
    }

    destroy() {
        window.removeEventListener('pointermove', this.onPointerMoveBound);
        window.removeEventListener('pointerup', this.onPointerUpBound);
        window.removeEventListener('mousemove', this.onMouseMoveBound);
        window.removeEventListener('mouseup', this.onMouseUpBound);
        window.removeEventListener('keydown', this.onKeyDownBound, true);
        document.removeEventListener('keydown', this.onKeyDownBound, true);
        window.removeEventListener('keyup', this.onKeyUpBound, true);
        document.removeEventListener('keyup', this.onKeyUpBound, true);
        this.events.off('evidenceComparison.open', this.onOpenBound);
        this.events.off('evidenceComparison.close', this.onCloseBound);
        this.events.off('evidenceComparison.toggle', this.onToggleBound);
        super.destroy();
    }
}

export { ComparisonSlider };
